import { identity } from 'lodash-es';
import { useMemo, useState } from 'react';
import { useBoolean } from 'react-use-boolean';
import { IDataExporter } from '../../libs/DataExporter';
import { invoiceIsPaid, invoiceIsUnPaid } from './invoiceTableConfig';
import { BillingTableItem, InvoiceCsvExportType } from './types';

const exportTypeConfig: Record<
  InvoiceCsvExportType,
  { name: string; filter: (invoices: BillingTableItem[]) => BillingTableItem[] }
> = {
  all: {
    name: 'All',
    filter: identity,
  },
  paid: {
    name: 'Paid',
    filter: (invoices) => invoices.filter(invoiceIsPaid),
  },
  unpaid: {
    name: 'Unpaid',
    filter: (invoices) => invoices.filter(invoiceIsUnPaid),
  },
};

const getInvoiceCsvName = (accountNumber: string, exportType: InvoiceCsvExportType) => {
  const now = new Date();
  const exportDate = `${now.getFullYear()}${now.getMonth() + 1}${now.getDate()}`;
  const exportTypeName = exportTypeConfig[exportType].name;
  return `${accountNumber}_invoiceSummary_${exportTypeName}_${exportDate}.csv`;
};

interface useInvoiceExportParams {
  invoices: BillingTableItem[];
  csvExporter: IDataExporter<BillingTableItem>;
  accountNumber: string;
  openFile: (dataString: string, name: string) => void;
}

export const useInvoiceExport = ({
  invoices,
  csvExporter,
  accountNumber,
  openFile,
}: useInvoiceExportParams) => {
  const [exportType, setExportType] = useState<InvoiceCsvExportType>('all');
  const [open, action] = useBoolean();

  const hasMultipleExport = useMemo(() => {
    const hasPaid = Boolean(invoices.find((i) => i.Balance === 0));
    const hasUnPaid = Boolean(invoices.find((i) => i.Balance !== 0));
    return hasPaid && hasUnPaid;
  }, [invoices]);

  const onExport = () => {
    action.off();

    const csvDataString = csvExporter.getCsvDataString(
      exportTypeConfig[exportType].filter(invoices),
    );
    openFile(getInvoiceCsvName(accountNumber, exportType), csvDataString);
  };

  const onOpen = () => {
    if (hasMultipleExport) {
      action.on();
      return;
    }
    const csvDataString = csvExporter.getCsvDataString(invoices);
    openFile(getInvoiceCsvName(accountNumber, 'all'), csvDataString);
  };

  return {
    open,
    onOpen,
    onClose: action.off,
    onExport,
    setExportType,
    exportType,
  };
};
